import React from "react";
import Icon from "./icons";
import DateConfigRow from "./DateConfigRow";
import { DATA_TYPES } from "./resourceTypes";
import { emptyDateConfig, emptyRow } from "./payload";

/**
 * The Key / Data Type / Value table. One component for all four places it
 * appears — Parameters, Headers, and the Form Data and x-www-form-urlencoded
 * bodies — so a change to how a row behaves lands in every tab at once.
 *
 * There is always one row on screen: removing the last one leaves a blank
 * rather than an empty table with nothing to type into. Blank rows are dropped
 * when the resource is saved, not while editing.
 *
 * A row typed as Date has no value to type: its Value box is disabled and a
 * DateConfigRow opens underneath, exactly as in the antd form.
 */
export default function ParamsTable({
  rows,
  onChange,
  keyPlaceholder = "Key",
  valuePlaceholder = "Value",
  errors = {},
}) {
  const list = rows && rows.length ? rows : [emptyRow()];

  const patch = (index, changes) => onChange(list.map((row, i) => (i === index ? { ...row, ...changes } : row)));

  /* Becoming a Date gives the row a config to fill in, and clears any value
     already typed: the run-time builders read `value` first and only fall back
     to dateConfig when it is empty, so a leftover value would quietly win over
     the date the user just configured.

     Leaving Date keeps the config rather than dropping it — flipping the type
     back and forth should not lose it, and the builders ignore a dateConfig on
     a row that is not a date. */
  const setDataType = (index, dataType) => {
    const row = list[index];
    if (dataType !== "date") {
      patch(index, { dataType });
      return;
    }
    patch(index, { dataType, value: "", dateConfig: row.dateConfig || emptyDateConfig() });
  };

  const remove = (index) => {
    const next = list.filter((_row, i) => i !== index);
    onChange(next.length ? next : [emptyRow()]);
  };

  return (
    <div className="rsce-kvt">
      <div className="rsce-kvt-head">
        <span>Key</span>
        <span>Data Type</span>
        <span>Value</span>
        <span aria-hidden="true" />
      </div>

      {list.map((row, index) => {
        const isDate = row.dataType === "date";
        const error = errors[index] || null;

        return (
          <div className={`rsce-kvt-line ${isDate ? "is-date" : ""}`} key={index}>
            <div className="rsce-kvt-row">
              <input
                className={`rsce-kvt-input ${error && error.field === "key" ? "has-error" : ""}`}
                type="text"
                placeholder={keyPlaceholder}
                value={row.key || ""}
                onChange={(e) => patch(index, { key: e.target.value })}
              />

              <div className="rsce-kvt-select-wrap">
                <select
                  className="rsce-kvt-select"
                  value={row.dataType || "string"}
                  onChange={(e) => setDataType(index, e.target.value)}
                >
                  {DATA_TYPES.map((t) => (
                    <option key={t.value} value={t.value}>
                      {t.label}
                    </option>
                  ))}
                </select>
                <span className="rsce-kvt-select-caret">{Icon.caret}</span>
              </div>

              {/* A date's value is worked out from the config below, so the box
                  is shown disabled rather than hidden — the row keeps its shape
                  and it is obvious why there is nothing to type. */}
              <input
                className={`rsce-kvt-input ${error && error.field === "value" && !isDate ? "has-error" : ""}`}
                type="text"
                placeholder={isDate ? "" : valuePlaceholder}
                value={isDate ? "" : row.value || ""}
                disabled={isDate}
                onChange={(e) => patch(index, { value: e.target.value })}
              />

              <button type="button" className="rsce-kvt-remove" title="Remove row" onClick={() => remove(index)}>
                {Icon.close}
              </button>
            </div>

            {/* A date row's value problem belongs to the config, so it is shown
                on that row rather than under the empty Value box. Anything about
                the key still reads under the row it is about. */}
            {isDate && (
              <DateConfigRow
                config={row.dateConfig}
                error={error && error.field === "value" ? error.message : null}
                onChange={(dateConfig) => patch(index, { dateConfig })}
              />
            )}

            {/* {error && (!isDate || error.field === "key") && <div className="rsce-kvt-error">{error.message}</div>} */}
            {/* On the same grid as the row, so the message sits under the box it
                is about — Key's under Key, Value's under Value — instead of
                always starting at the left edge. */}
            {error && (!isDate || error.field === "key") && (
              <div className="rsce-kvt-error-row">
                <div className={`rsce-kvt-error ${error.field === "value" ? "is-value" : "is-key"}`}>
                  {error.message}
                </div>
              </div>
            )}
          </div>
        );
      })}

      <button type="button" className="rsce-kvt-add" onClick={() => onChange(list.concat(emptyRow()))}>
        {Icon.plus}
        Add row
      </button>
    </div>
  );
}
